import { Component } from 'react';
import { Row, Col, message } from 'antd';
import { getLangList } from '@/lib/i18n';
import { ReButton, ReModal } from '@/components/refineElement';
import RentConfig from "@/config/rent";
import ResponsiveTable from '@/components/responsiveTable';
import { showDelConfirm, getOnOkFunction } from "@/lib/antdElementFunction";
import { hasPermission } from "@/lib/permissionUtil";
import ModalItem from './ModalItem';
import ModalRent from './ModalRent';

const { getAllItemInfo, createItemInfo, updateItemInfo, deleteItemInfo, rentItem } = RentConfig;

interface props {
  selectedGroupId: any,
}

interface state {
  isModalVisible: boolean;
  isModalRentVisible: boolean;
  handleType: string;
  itemArray: any;
  itemInfo: any;
}

class Group extends Component<props, state> {
  state = {
    isModalVisible: false,
    isModalRentVisible: false,
    handleType: '',
    itemArray: [] as never as any,
    itemInfo: {} as never as any,
  };
  rentT: any = getLangList("rent");
  errorT: any = getLangList("error");
  buttonT: any = getLangList("button");

  componentDidMount() {
    this.getAllItemInfo();
  }

  columns = [
    { title: this.rentT.ITEM, dataIndex: "itemName" },
    { title: this.rentT.DESCRIPTION, dataIndex: "itemDescription" },
    {
      title: this.rentT.STATUS,
      dataIndex: "userAccountId",
      render: (text: string) => text ? this.rentT.RENTING : this.rentT.CAN_RENT,
    },
    {
      title: this.rentT.RENT_PERSON,
      dataIndex: "nameCn",
      render: (text: string, record: any) => {
        const { nameCn, nameEn } = record;
        const name = (nameCn && nameEn) ? `${nameCn}-${nameEn}` : '';
        return name;
      }
    },
    { title: this.rentT.RENT_TIME, dataIndex: "rentTime" },
    {
      title: this.rentT.RETURN_TIME,
      dataIndex: "returnTime",
      render: (text: any, record: any) => {
        const { userAccountId } = record;
        return userAccountId ? '' : text;
      }
    },
    {
      title: this.rentT.OPERATE,
      dataIndex: "action",
      render: (text: any, record: any) => {
        const { itemId, userAccountId } = record;
        return (
          <div className="action-bar">
            <div>
              {userAccountId
                ? (
                  <ReButton
                    iconName='copy'
                    onClick={this.clickReturn(record)}
                  >
                    {this.buttonT.RETURN}
                  </ReButton>
                )
                : (
                  <ReButton
                    iconName='copy'
                    onClick={this.clickRent(record)}
                  >
                    {this.buttonT.RENT}
                  </ReButton>
                )
              }
            </div>
            {hasPermission('RENT_EDIT') &&
              <div>
                <ReButton
                  iconName='edit'
                  onClick={this.clickEdit(record)}
                >
                  {this.buttonT.EDIT}
                </ReButton>
              </div>
            }
            {hasPermission('RENT_DELETE') &&
              <div>
                <ReButton
                  iconName='delete'
                  onClick={this.clickDelete(itemId)}
                  attributeObject={{ type: 'danger' }}
                >
                  {this.buttonT.DELETE}
                </ReButton>
              </div>
            }
          </div >
        );
      }
    },
  ];

  getAllItemInfo = () => {
    const { selectedGroupId } = this.props;
    const params = { itemGroupId: selectedGroupId };

    getAllItemInfo(params).then((res: any) => {
      const itemArray = res?.value ?? [];
      this.setState({ itemArray });
    });
  }

  toggleModal = (handleType: string = '') => {
    const { isModalVisible } = this.state;
    this.setState({ isModalVisible: !isModalVisible, handleType });
  }

  toggleModalRent = (handleType: string = '') => {
    const { isModalRentVisible } = this.state;
    this.setState({ isModalRentVisible: !isModalRentVisible, handleType });
  }

  clickCreate = () => {
    this.setState({ itemInfo: {} }, () => this.toggleModal('create'));
  }

  clickEdit = (data: object) => () => {
    this.setState({ itemInfo: data }, () => this.toggleModal('update'));
  }

  clickDelete = (id: string) => () => {
    showDelConfirm(() => this.deleteItemInfo(id));
  };

  clickRent = (data: object) => () => {
    this.setState({ itemInfo: data }, () => this.toggleModalRent('rent'));
  }

  clickReturn = (data: object) => () => {
    this.setState({ itemInfo: data }, () => this.toggleModalRent('return'));
  }

  createItemInfo = (values: any) => {
    const { selectedGroupId } = this.props;
    const { itemName, itemDescription } = values;
    const params = {
      itemName,
      itemDescription,
      itemGroupId: selectedGroupId,
    };

    createItemInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(this.errorT.failed);
      }
      message.success(this.errorT.success);
      this.toggleModal();
      this.getAllItemInfo();
    });
  }

  updateItemInfo = (values: any) => {
    const { itemInfo } = this.state;
    const { itemId } = itemInfo;
    const { itemName, itemDescription } = values;
    const params = { itemName, itemDescription, itemId };

    updateItemInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(this.errorT.failed);
      }
      message.success(this.errorT.success);
      this.toggleModal();
      this.getAllItemInfo();
    });
  }

  deleteItemInfo = (id: string) => {
    const params = { itemId: id };

    deleteItemInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(this.errorT.failed);
      }
      message.success(this.errorT.success);
      this.getAllItemInfo();
    });
  }

  rentAndReturnItem = (values: any) => {
    const { itemInfo, handleType } = this.state;
    const { itemId } = itemInfo;
    const { date, rentBy, lessee } = values;
    const params: any = { itemId };

    handleType === 'return' && (params.returnTime = date);
    handleType === 'rent' && (params.rentTime = date);
    params[rentBy] = lessee;

    rentItem(params).then((res: any) => {
      if (!res.success) {
        return message.error(this.errorT.failed);
      }
      message.success(this.errorT.success);
      this.toggleModalRent();
      this.getAllItemInfo();
    });
  }

  render() {
    const { isModalVisible, isModalRentVisible, itemArray, itemInfo, handleType } = this.state;
    const okFunction = {
      create: this.createItemInfo,
      update: this.updateItemInfo,
    };

    return (
      <>
        <Row gutter={[0, 8]}>
          <Col span={24}>
            <div className="re-button-container-right">
              {hasPermission('RENT_NEW') &&
                <ReButton iconName='edit' onClick={this.clickCreate}>
                  {this.rentT.NEW_ITEM}
                </ReButton>
              }
            </div>
          </Col>
          <Col span={24}>
            <ResponsiveTable
              dataSource={itemArray}
              columns={this.columns}
            />
          </Col>
        </Row>
        {isModalVisible &&
          <ReModal
            title={this.rentT.ITEM}
            onCancel={() => this.toggleModal()}
            visible={isModalVisible}
            onOk={(data: any) => getOnOkFunction(handleType, data, okFunction)}
            CollectedForm={ModalItem}
            formProps={{
              handleType,
              itemInfo,
            }}
            width={'700px'}
          />
        }
        {isModalRentVisible &&
          <ReModal
            title={handleType === 'rent' ? this.rentT.RENT : this.rentT.RETURN}
            onCancel={() => this.toggleModalRent()}
            visible={isModalRentVisible}
            onOk={this.rentAndReturnItem}
            CollectedForm={ModalRent}
            formProps={{
              handleType,
              rentAndReturnItem: this.rentAndReturnItem,
            }}
            width={'700px'}
          />
        }
      </>
    );
  }
}

export default Group;